import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Phone, MessageCircle, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AnimatedSection } from "./AnimatedSection";
import { CONTACT } from "@/lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: "accounting", label: "Accounting" },
  { value: "auditing", label: "Auditing" },
  { value: "secretarial", label: "Secretarial" },
  { value: "general", label: "Just a general question" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", service_need: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service_need) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thanks! We'll call you back shortly.");
      setForm({ name: "", phone: "", service_need: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#0A1128] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16">
        <AnimatedSection>
          <p className="overline text-[#C5A059] mb-4">Get In Touch</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-white mb-4">
            Let&apos;s get your accounts sorted.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-10 max-w-md">
            Send us a message or request a call. We will listen to what you need and tell you exactly
            how we can help. No pressure.
          </p>

          <div className="space-y-4 mb-10">
            <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-3 text-slate-200 text-base hover:text-[#C5A059] transition-colors">
              <Phone size={18} className="text-[#C5A059] shrink-0" />
              {CONTACT.phoneDisplay}
            </a>
            <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 text-slate-200 text-base hover:text-[#C5A059] transition-colors">
              <MessageCircle size={18} className="text-[#C5A059] shrink-0" />
              {CONTACT.phoneDisplay} (WhatsApp)
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-3 text-slate-200 text-base hover:text-[#C5A059] transition-colors">
              <Mail size={18} className="text-[#C5A059] shrink-0" />
              {CONTACT.email}
            </a>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.15} className="bg-white rounded-2xl p-8 md:p-10 self-start">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
            <div>
              <label className="text-base font-medium text-slate-600">Name</label>
              <Input
                data-testid="contact-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                className="mt-1.5 h-11 text-base"
              />
            </div>
            <div>
              <label className="text-base font-medium text-slate-600">Phone Number</label>
              <Input
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Your phone number"
                type="tel"
                className="mt-1.5 h-11 text-base"
              />
            </div>
            <div>
              <label className="text-base font-medium text-slate-600">What do you need help with?</label>
              <Select value={form.service_need} onValueChange={(v) => setForm({ ...form, service_need: v })}>
                <SelectTrigger data-testid="contact-service-select" className="mt-1.5 h-11 text-base">
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`service-option-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="w-full rounded-full bg-[#0A1128] hover:bg-[#0A1128]/90 text-white h-12 text-base md:text-lg"
            >
              {loading ? "Sending..." : "Please Call Me Back"}
            </Button>
          </form>
        </AnimatedSection>
      </div>
    </section>
  );
};
